import { BarChart3, Coins, ShoppingBag, TrendingUp, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ROTA_RELATORIO, reais } from "./api";
import Indicador from "./Indicador";

/**
 * Resultado dos anúncios no período: gasto (Meta, já com imposto) × pedidos e
 * faturamento (Núcleo), e a lista de campanhas. Linha "Meta — campanha não
 * identificada" = pedido do link Anúncios Meta sem os parâmetros do anúncio.
 */
const VERDE = "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
const CINZA = "bg-muted text-muted-foreground";
const AMARELO = "bg-amber-500/15 text-amber-600 dark:text-amber-400";
const VERMELHO = "bg-red-500/10 text-red-500";
const STATUS = {
  ACTIVE: ["No ar", VERDE],
  ENCERRADA: ["Encerrada", CINZA],
  PAUSED: ["Pausada", CINZA],
  CAMPAIGN_PAUSED: ["Pausada", CINZA],
  ADSET_PAUSED: ["Pausada", CINZA],
  PENDING_REVIEW: ["Em análise", AMARELO],
  IN_PROCESS: ["Em análise", AMARELO],
  DISAPPROVED: ["Reprovada", VERMELHO],
  WITH_ISSUES: ["Precisa de atenção", VERMELHO],
  PENDING_BILLING_INFO: ["Falta pagamento", VERMELHO],
  ARCHIVED: ["Arquivada", CINZA],
  DELETED: ["Apagada", CINZA],
  desconhecida: ["Não lida ainda", CINZA],
  sem_parametros: ["Sem os parâmetros", AMARELO],
};
const PERIODOS = [7, 30, 90];

// ROI = faturamento ÷ investido, em "x" (decisão do dono em 04/10/2026: o nome é ROI, a conta é esta).
// Abaixo de 10 mostra duas casas: 0,01x não pode virar "0x".
const retornoFmt = (r) => (r == null ? "—" : `${r.toLocaleString("pt-BR", { maximumFractionDigits: r < 10 ? 2 : 1 })}x`);

function Linha({ c }) {
  const [rotulo, cor] = STATUS[c.status] || [c.status || "—", CINZA];
  return (
    <tr className="border-t" data-e2e="meta-campanha">
      <td className="py-2 pr-3 max-w-[240px] truncate font-medium" title={c.meta_campaign_id || ""}>{c.nome}</td>
      <td className="py-2 pr-3"><span className={`inline-flex whitespace-nowrap text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cor}`}>{rotulo}</span></td>
      <td className="py-2 pr-3 text-right tabular-nums">{reais(c.investido_centavos)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{c.cliques_link}</td>
      <td className="py-2 pr-3 text-right tabular-nums font-semibold">{c.pedidos}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{reais(c.receita_centavos)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{c.custo_por_pedido_centavos == null ? "—" : reais(c.custo_por_pedido_centavos)}</td>
      <td className="py-2 text-right tabular-nums font-semibold">{retornoFmt(c.retorno)}</td>
    </tr>
  );
}

export default function ListaCampanhas({ dados, dias, onDias }) {
  const navigate = useNavigate();
  const linhas = dados?.campanhas || [];
  const t = dados?.totais || {};
  return (
    <section className="space-y-3" data-e2e="meta-campanhas">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold">Resultado dos anúncios</h2>
        <div className="inline-flex rounded-lg border bg-card p-0.5">
          {PERIODOS.map((d) => (
            <button key={d} onClick={() => onDias(d)}
              className={`rounded-md px-3 py-1 text-xs font-medium ${dias === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {d} dias
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Indicador rotulo="Investido" valor={reais(t.investido_centavos)} dica={`gasto ${reais(t.gasto_centavos)} + imposto`} icone={Wallet} e2e="meta-kpi-investido" />
        <Indicador rotulo="Pedidos" valor={String(t.pedidos ?? 0)} dica="vindos do anúncio" icone={ShoppingBag} e2e="meta-kpi-pedidos" />
        <Indicador rotulo="Faturamento" valor={reais(t.receita_centavos)} dica="dos pedidos vindos do anúncio" icone={Coins} destaque="bg-sky-500/10 border-sky-500/30" e2e="meta-kpi-faturamento" />
        <Indicador rotulo="ROI" valor={retornoFmt(t.retorno)} dica="faturamento ÷ investido" icone={TrendingUp} e2e="meta-kpi-retorno" />
      </div>
      <div className="rounded-xl border bg-card p-4 sm:p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">Campanhas</h3>
          <button onClick={() => navigate(ROTA_RELATORIO)} data-e2e="meta-ver-relatorio"
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:border-primary">
            <BarChart3 className="w-3.5 h-3.5" /> Ver relatório completo
          </button>
        </div>
        {linhas.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma campanha na conta ainda. Quando você anunciar, ela aparece aqui com o gasto e os pedidos.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1.5 pr-3 font-medium">Campanha</th><th className="py-1.5 pr-3 font-medium">Status</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Investido</th><th className="py-1.5 pr-3 font-medium text-right">Cliques</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Pedidos</th><th className="py-1.5 pr-3 font-medium text-right">Faturamento</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Custo/pedido</th><th className="py-1.5 font-medium text-right">ROI</th>
                </tr>
              </thead>
              <tbody>{linhas.map((c) => <Linha key={c.meta_campaign_id || "nao-identificada"} c={c} />)}</tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          Pedidos e faturamento são os que o Núcleo viu chegar pelo link do anúncio (último clique em 7 dias; cancelado não conta).
          O número da Meta costuma ser diferente — ela conta também quem só viu o anúncio.
        </p>
      </div>
    </section>
  );
}
